import React from 'react';
import { Layers, Shield, Database, Cloud, Lock, Factory } from 'lucide-react';

const CAMADAS = [
  {
    titulo: 'Interface',
    texto: 'Aplicação React publicada em hosting estático. O endereço de uso é regulamais.app.br. O navegador só entrega a tela; a regra de negócio não fica no HTML.',
  },
  {
    titulo: 'API',
    texto: 'Cloud Functions em Node.js 20, região us-central1. A tela chama funções nomeadas (callables). Cada função exige usuário autenticado e um papel permitido.',
  },
  {
    titulo: 'Dados',
    texto: 'Cloud Firestore na região southamerica-east1. O documento de empresa é o tenant. Quase todo registro operacional carrega empresa_id ou id_empresa.',
  },
  {
    titulo: 'Arquivos',
    texto: 'Cloud Storage guarda fotos e PDFs: recebimento, documentos de fornecedor, apontamento de produção, monitoramento e manutenção. O restante do bucket é recusado.',
  },
];

const PAPEIS = [
  ['superadmin', 'Vê todas as empresas, o painel de migração e esta página. Não fica limitado pelos módulos da empresa.'],
  ['admin / administrador / gerente', 'Operam a própria empresa: cadastros, usuários e a operação liberada pelos módulos.'],
  ['qualidade', 'Conferência, documentos, monitoramento, relatórios de produção e não conformidades.'],
  ['producao', 'Quiosque, pesagem, fluxo por setor e apontamentos.'],
  ['recebedor', 'Entrada de insumos.'],
  ['fornecedor', 'Os próprios documentos, cotações e o que a regra liga ao id_fornecedor.'],
  ['manutencao / fornecedor_equipamentos', 'Equipamentos, plano e cotações de manutenção.'],
];

const PASSOS = [
  'A pessoa entra com Firebase Authentication. A senha não fica no Firestore.',
  'O perfil em Usuarios define função, empresa e se a conta está ativa. Isso também vai para os custom claims do token.',
  'O menu é o cruzamento do teto da empresa (módulos) com o papel. Sem a chave gravada, vale o padrão do módulo.',
  'A tela chama uma função. A função lê o papel de novo em Usuarios e recusa quem está inativo ou fora da lista.',
  'A leitura direta do Firestore, quando existe, passa pelas security rules: autenticação, papel e empresa do documento.',
];

const BLOCOS = [
  ['Cadastros', 'Empresas, usuários, fornecedores, insumos, produtos finais, setores e equipamentos.'],
  ['Compras e entrada', 'Cotações, recebimento, decisão da qualidade e entrada no estoque.'],
  ['Armazém', 'Câmara, rua, estante, andar, posição e pallet, com QR na operação.'],
  ['Produção', 'Planejamento, ordem, pesagem, carrinho por setor, parâmetros, fotos e peso final.'],
  ['BPF', 'Monitoramento (água, limpeza, temperatura) e expedição de produto acabado.'],
  ['Documentos', 'Tipos obrigatórios, envio do fornecedor e aprovação.'],
];

function Cartao({ titulo, texto }) {
  return (
    <article className="rounded-xl border bg-white p-4">
      <h3 className="font-semibold text-gray-900">{titulo}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{texto}</p>
    </article>
  );
}

const ArquiteturaPage = () => (
  <div className="space-y-8 p-6">
    <div>
      <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
        <Layers className="h-6 w-6 text-blue-700" /> Arquitetura da solução
      </h1>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-500">
        QualiManager é um sistema multiempresa de gestão da qualidade e da produção. Cada indústria é uma empresa.
        Usuários, estoque, produção e documentos ficam separados por esse vínculo.
      </p>
    </div>

    <section>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Cloud className="h-5 w-5 text-blue-700" /> Camadas
      </h2>
      <div className="grid gap-3 md:grid-cols-2">
        {CAMADAS.map((item) => (
          <Cartao key={item.titulo} titulo={item.titulo} texto={item.texto} />
        ))}
      </div>
    </section>

    <section className="rounded-xl border bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Lock className="h-5 w-5 text-blue-700" /> Caminho de uma operação
      </h2>
      <ol className="space-y-2 text-sm text-gray-700">
        {PASSOS.map((passo, indice) => (
          <li key={passo} className="flex gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-800">
              {indice + 1}
            </span>
            <span className="leading-relaxed">{passo}</span>
          </li>
        ))}
      </ol>
    </section>

    <section>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Shield className="h-5 w-5 text-blue-700" /> Segurança
      </h2>
      <div className="grid gap-3 lg:grid-cols-2">
        <Cartao
          titulo="Identidade"
          texto="Login pelo Firebase Authentication. O papel aceito é superadmin, admin, administrador, gerente, qualidade, producao, recebedor, fornecedor, manutencao ou fornecedor_equipamentos. Conta com ativo falso é recusada na função."
        />
        <Cartao
          titulo="Isolamento da empresa"
          texto="O token e o documento do usuário carregam a empresa. As funções usam esse identificador, e o superadmin é quem pode informar outra empresa. O menu some quando o módulo da empresa está desligado."
        />
        <Cartao
          titulo="Firestore"
          texto="As rules exigem usuário autenticado. Superadmin passa pelo token ou pelo documento Usuarios. Os demais só leem e gravam o que a regra liga à própria empresa ou ao próprio fornecedor. Coleção sem regra não fica aberta."
        />
        <Cartao
          titulo="Storage"
          texto="Fotos e documentos só entram em pastas previstas: documents, documentos, recebimentos, produtos, produção, manutenção e monitoramento. Nessas pastas, a regra atual exige apenas usuário autenticado. Qualquer outro caminho é negado."
        />
      </div>
      <div className="mt-3 overflow-x-auto rounded-xl border bg-white">
        <h3 className="border-b px-4 py-3 text-sm font-semibold text-gray-900">Criptografia em trânsito e em repouso</h3>
        <p className="px-4 py-3 text-sm leading-relaxed text-gray-600">
          A cifra é a da plataforma Google Cloud, com chaves gerenciadas pelo Google. O QualiManager não aplica uma cifra própria em cada campo antes de gravar. Quem passa na regra de acesso lê o valor.
        </p>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Trecho</th>
              <th className="px-3 py-2">Em trânsito</th>
              <th className="px-3 py-2">Em repouso</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t align-top">
              <td className="px-3 py-2 font-medium text-gray-900">Site</td>
              <td className="px-3 py-2 text-gray-600">HTTPS com TLS. O certificado de regulamais.app.br é o do Firebase Hosting.</td>
              <td className="px-3 py-2 text-gray-600">Arquivos estáticos cifrados em disco pelo Google.</td>
            </tr>
            <tr className="border-t align-top">
              <td className="px-3 py-2 font-medium text-gray-900">Firestore, Storage, Functions e Authentication</td>
              <td className="px-3 py-2 text-gray-600">TLS. O navegador e as funções só falam com esses serviços por canal criptografado.</td>
              <td className="px-3 py-2 text-gray-600">AES-256 com chave gerenciada pelo Google, antes da gravação em disco.</td>
            </tr>
            <tr className="border-t align-top">
              <td className="px-3 py-2 font-medium text-gray-900">Senha de acesso</td>
              <td className="px-3 py-2 text-gray-600">TLS até o Firebase Authentication.</td>
              <td className="px-3 py-2 text-gray-600">Hash scrypt do Firebase. A senha não é gravada no Firestore.</td>
            </tr>
            <tr className="border-t align-top">
              <td className="px-3 py-2 font-medium text-gray-900">Arquivo de migração</td>
              <td className="px-3 py-2 text-gray-600">O download sai por HTTPS.</td>
              <td className="px-3 py-2 text-gray-600">O zip no computador do superadmin não tem senha. Os JSON ficam legíveis para quem guardar o arquivo.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-3 overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Papel</th>
              <th className="px-3 py-2">Alcance</th>
            </tr>
          </thead>
          <tbody>
            {PAPEIS.map(([papel, alcance]) => (
              <tr key={papel} className="border-t">
                <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">{papel}</td>
                <td className="px-3 py-2 text-gray-600">{alcance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Factory className="h-5 w-5 text-blue-700" /> Blocos funcionais
      </h2>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {BLOCOS.map(([titulo, texto]) => (
          <Cartao key={titulo} titulo={titulo} texto={texto} />
        ))}
      </div>
    </section>

    <section className="rounded-xl border bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Database className="h-5 w-5 text-blue-700" /> Dados e publicação
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-gray-600">
        <p>
          O fluxo de produção grava carrinho, apontamento, parâmetro, foto e peso final. O relatório lê esses apontamentos.
          O quiosque escuta os carrinhos em tempo real e grava a ação pela função, com o operador escolhido no setor.
        </p>
        <p>
          A interface e as funções são publicadas separadas, no mesmo projeto. A tela em uso é a versão estática mais recente;
          o index não fica em cache longo, para o tablet pegar a versão nova ao reabrir a aba.
        </p>
        <p>
          O painel de migração, visível só ao superadmin, exporta o esquema e os documentos de cada coleção. Esta página não altera dados.
        </p>
      </div>
    </section>
  </div>
);

export default ArquiteturaPage;
